// TwoFactorAuth.js
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import {
    Box,
    Button,
    TextField,
    Switch,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Typography,
    Paper,
    Alert,
    Grid,
    CircularProgress
} from '@mui/material';

const TwoFactorAuth = () => {
    const [loading, setLoading] = useState(false);
    const [is2FAEnabled, setIs2FAEnabled] = useState('no');
    const [showQRCode, setShowQRCode] = useState(false);
    const [otpauthUrl, setOtpauthUrl] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [otp, setOTP] = useState('');

    // Fetch initial 2FA status
    useEffect(() => {
        const fetchTwoFAStatus = async () => {
            try {
                const token = Cookies.get('token');
                const response = await axios.get('/api/user/2fa-status', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setIs2FAEnabled(response.data.is_twofa_enabled || 'no');
            } catch (err) {
                console.error('2FA Status Error:', err);
                setError('Failed to fetch 2FA status');
            }
        };

        fetchTwoFAStatus();
    }, []);

    // Handle switch toggle
    const handleSwitchToggle = () => {
        if (is2FAEnabled === 'yes') {
            // handleDisable2FA();
            setShowOTPInput(true);
            setError('');
            setSuccess('');
        } else {
            handle2FASetup();
        }
    };

    const handle2FASetup = async () => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('token');
            const response = await axios.post('/api/setup-2fa', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setOtpauthUrl(response.data.otpauthUrl);
                setShowQRCode(true);
            }
        } catch (err) {
            console.error('2FA Setup Error:', err);
            setError('Failed to setup 2FA. Please try again.');
            setIs2FAEnabled('no');
        } finally {
            setLoading(false);
        }
    };

    const handleVerification = async () => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('token');
            const response = await axios.post('/api/verify-2fa',
                { token: verificationCode },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setIs2FAEnabled('yes');
                setShowQRCode(false);
                setSuccess('2FA has been successfully enabled');
                setVerificationCode('');

                // Refresh 2FA status
                const statusResponse = await axios.get('/api/user/2fa-status', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setIs2FAEnabled(statusResponse.data.is_twofa_enabled);
            }
        } catch (err) {
            console.error('2FA Verification Error:', err);
            setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
            setIs2FAEnabled('no');
        } finally {
            setLoading(false);
        }
    };


    const handleDisable2FA = async () => {
        try {
            setLoading(true);
            setError('');

            // Get the authentication token from cookies
            const token = Cookies.get('token');

            if (!token) {
                setError('Authentication token not found. Please login again.');
                return;
            }

            const response = await axios.post('/api/disable-2fa',
                { token: otp },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setIs2FAEnabled('no');
                setShowOTPInput(false);
                setOTP('');
                setSuccess('2FA has been successfully disabled');

                // Optionally refresh 2FA status
                const statusResponse = await axios.get('/api/user/2fa-status', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setIs2FAEnabled(statusResponse.data.is_twofa_enabled);
            }
        } catch (err) {
            console.error('Error disabling 2FA:', err);
            setError(err.response?.data?.message || 'Failed to disable 2FA. Please try again.');

            // Handle 401 specifically
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
                // Optionally redirect to login page or handle session expiry
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', my: 4 }}>
            <Typography variant="h5" gutterBottom>
                Two-Factor Authentication (2FA)
            </Typography>

            {(error || success) && (
                <Alert
                    severity={error ? "error" : "success"}
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setError('');
                        setSuccess('');
                    }}
                >
                    {error || success}
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Switch
                    checked={is2FAEnabled === 'yes'}
                    onChange={handleSwitchToggle}
                    color="primary"
                    disabled={loading}
                />
                <Typography sx={{ ml: 1 }}>
                    {is2FAEnabled === 'yes' ? 'Disable' : 'Enable'} Two-Factor Authentication
                </Typography>
                {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Box>
            {showOTPInput && (
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Enter OTP"
                        value={otp}
                        onChange={(e) => setOTP(e.target.value.replace(/\D/g, ''))}
                        fullWidth
                        margin="normal"
                        type="text"
                        inputProps={{
                            maxLength: 6,
                            pattern: '[0-9]*'
                        }}
                        helperText="Enter the 6-digit code from your authenticator app"
                    />
                    <Button
                        variant="contained"
                        onClick={handleDisable2FA}
                        disabled={loading || otp.length !== 6}
                        sx={{ mt: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Verify and Disable 2FA'}
                    </Button>
                </Box>
            )}
            <Dialog
                open={showQRCode}
                onClose={() => !loading && setShowQRCode(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Set Up Two-Factor Authentication
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                        {otpauthUrl && (
                            <Box sx={{ mb: 3 }}>
                                <QRCodeSVG
                                    value={otpauthUrl}
                                    size={256}
                                    level="H"
                                    includeMargin={true}
                                />
                            </Box>
                        )}
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            1. Install an authenticator app like Google Authenticator or Authy
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            2. Scan the QR code with your authenticator app
                        </Typography>
                        <TextField
                            fullWidth
                            label="Enter 6-digit Verification Code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                            margin="normal"
                            inputProps={{
                                maxLength: 6,
                                pattern: '[0-9]*'
                            }}
                            helperText="Enter the code shown in your authenticator app"
                            disabled={loading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowQRCode(false);
                            setVerificationCode('');
                            setIs2FAEnabled('no');
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleVerification}
                        color="primary"
                        disabled={loading || verificationCode.length !== 6}
                        variant="contained"
                    >
                        {loading ? <CircularProgress size={24} /> : 'Verify and Enable'}
                    </Button>
                </DialogActions>
            </Dialog>

            {is2FAEnabled === 'yes' && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                        ✓ Two-factor authentication is currently enabled
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        You'll be asked for a verification code when signing in to your account
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default TwoFactorAuth;

